import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import { formatDate } from '../../utils/dateUtils';

const EditLearningPlanModal = ({ plan, onClose, onSuccess }) => {
  // Helper function to convert date to UK timezone YYYY-MM-DD string
  const toUKDateString = (dateInput) => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    // Convert to UK timezone and extract date part
    return date.toLocaleDateString('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).split('/').reverse().join('-'); // Convert DD/MM/YYYY to YYYY-MM-DD
  };

  // Active tab state
  const [activeTab, setActiveTab] = useState('timeline');

  // Form state - initialize with current plan values
  const [formData, setFormData] = useState({
    timeline: {
      start: toUKDateString(plan.timeline.start),
      end: toUKDateString(plan.timeline.end)
    },
    schedule: {
      daysPerWeek: plan.schedule.daysPerWeek,
      specificDays: [...plan.schedule.specificDays]
    },
    goal: {
      type: plan.goal.type,
      pages: plan.goal.pages ? { ...plan.goal.pages } : null,
      verses: plan.goal.verses ? { ...plan.goal.verses } : null,
      surahs: plan.goal.surahs ? [...plan.goal.surahs] : null,
      lines: plan.goal.lines ? { ...plan.goal.lines } : null
    },
    notes: plan.notes || ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Check for changes
  useEffect(() => {
    // Sort arrays before comparison to avoid false positives
    const sortedFormDays = [...formData.schedule.specificDays].sort();
    const sortedPlanDays = [...plan.schedule.specificDays].sort();

    const changed =
      formData.timeline.start !== toUKDateString(plan.timeline.start) ||
      formData.timeline.end !== toUKDateString(plan.timeline.end) ||
      formData.schedule.daysPerWeek !== plan.schedule.daysPerWeek ||
      JSON.stringify(sortedFormDays) !== JSON.stringify(sortedPlanDays) ||
      formData.notes !== (plan.notes || '');

    setHasChanges(changed);
  }, [formData, plan]);

  // Handle schedule day toggle
  const toggleScheduleDay = (day) => {
    setFormData(prev => {
      const days = [...prev.schedule.specificDays];
      const index = days.indexOf(day);

      if (index > -1) {
        days.splice(index, 1);
      } else {
        days.push(day);
      }

      return {
        ...prev,
        schedule: {
          ...prev.schedule,
          specificDays: days,
          daysPerWeek: days.length
        }
      };
    });
  };

  // Validate form
  const validateForm = () => {
    // Timeline validation
    const startDate = new Date(formData.timeline.start);
    const endDate = new Date(formData.timeline.end);

    if (endDate <= startDate) {
      setValidationError('End date must be after start date');
      return false;
    }

    // Schedule validation
    if (formData.schedule.specificDays.length === 0) {
      setValidationError('Please select at least one day of the week');
      return false;
    }

    setValidationError('');
    return true;
  };

  // Show confirmation dialog before submitting
  const handleSubmitClick = () => {
    if (!validateForm()) {
      return;
    }
    setShowConfirmation(true);
  };

  // Handle actual form submission
  const handleConfirmedSubmit = async () => {
    setShowConfirmation(false);

    try {
      setLoading(true);
      setError('');

      // Build updates object - only send changed fields
      const updates = {};

      // Timeline changes - only send changed fields
      const startChanged = formData.timeline.start !== toUKDateString(plan.timeline.start);
      const endChanged = formData.timeline.end !== toUKDateString(plan.timeline.end);

      if (startChanged || endChanged) {
        updates.timeline = {};
        if (startChanged) {
          updates.timeline.start = formData.timeline.start;
        }
        if (endChanged) {
          updates.timeline.end = formData.timeline.end;
        }
      }

      // Schedule changes
      const sortedFormDays = [...formData.schedule.specificDays].sort();
      const sortedPlanDays = [...plan.schedule.specificDays].sort();
      if (formData.schedule.daysPerWeek !== plan.schedule.daysPerWeek ||
          JSON.stringify(sortedFormDays) !== JSON.stringify(sortedPlanDays)) {
        updates.schedule = {
          daysPerWeek: formData.schedule.daysPerWeek,
          specificDays: formData.schedule.specificDays
        };
      }

      // Notes changes
      if (formData.notes !== (plan.notes || '')) {
        updates.notes = formData.notes;
      }

      // Validate updates object is not empty
      if (Object.keys(updates).length === 0) {
        setError('No changes detected. Please modify at least one field.');
        return;
      }

      // Call API
      const response = await api.put(`/learning-plans/${plan._id}/edit-and-regenerate`, {
        updates
      });

      if (response.data.success) {
        const { plan: updatedPlan, regenerationSummary } = response.data.data;
        const message = `Plan updated! ${regenerationSummary.completedAssignmentsPreserved} completed assignment(s) preserved, ${regenerationSummary.newAssignmentsCreated} new assignment(s) created.`;
        onSuccess(updatedPlan, message);
      }
    } catch (err) {
      console.error('Error updating plan:', err);
      // Distinguish network errors from server errors
      if (!err.response) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to update plan. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate duration in days
  const calculateDuration = () => {
    if (!formData.timeline.start || !formData.timeline.end) return 0;
    const start = new Date(formData.timeline.start);
    const end = new Date(formData.timeline.end);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Check if form has any completions (to disable start date)
  const hasCompletedAssignments = plan.progress?.completed > 0;

  // ESC key handler for accessibility
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && !showConfirmation && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [showConfirmation, loading, onClose]);

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        overflowY: 'auto',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        className="modal-container"
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <h2 style={{margin: 0, fontSize: '24px', color: '#333'}}>
            ✏️ Edit Learning Plan
          </h2>
          <p style={{margin: '8px 0 0 0', fontSize: '14px', color: '#666'}}>
            Editing plan for: <strong>{plan.studentId?.name || 'Student'}</strong>
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          <button
            onClick={() => setActiveTab('timeline')}
            style={{
              flex: 1,
              padding: '16px',
              border: 'none',
              backgroundColor: activeTab === 'timeline' ? 'white' : 'transparent',
              borderBottom: activeTab === 'timeline' ? '3px solid #2196f3' : 'none',
              fontWeight: activeTab === 'timeline' ? '600' : '400',
              color: activeTab === 'timeline' ? '#2196f3' : '#666',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            📅 Timeline
          </button>
          <button
            onClick={() => setActiveTab('schedule')}
            style={{
              flex: 1,
              padding: '16px',
              border: 'none',
              backgroundColor: activeTab === 'schedule' ? 'white' : 'transparent',
              borderBottom: activeTab === 'schedule' ? '3px solid #2196f3' : 'none',
              fontWeight: activeTab === 'schedule' ? '600' : '400',
              color: activeTab === 'schedule' ? '#2196f3' : '#666',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            🗓️ Schedule
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            style={{
              flex: 1,
              padding: '16px',
              border: 'none',
              backgroundColor: activeTab === 'notes' ? 'white' : 'transparent',
              borderBottom: activeTab === 'notes' ? '3px solid #2196f3' : 'none',
              fontWeight: activeTab === 'notes' ? '600' : '400',
              color: activeTab === 'notes' ? '#2196f3' : '#666',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            📝 Notes
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '24px',
          overflowY: 'auto'
        }}>
          {/* Error Messages */}
          {error && (
            <div style={{
              marginBottom: '20px',
              padding: '12px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '6px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {validationError && (
            <div style={{
              marginBottom: '20px',
              padding: '12px',
              backgroundColor: '#fff3e0',
              color: '#e65100',
              borderRadius: '6px',
              fontSize: '14px'
            }}>
              {validationError}
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div>
              <h3 style={{marginTop: 0, fontSize: '18px', color: '#333'}}>Timeline Settings</h3>
              <p style={{fontSize: '13px', color: '#666', marginBottom: '20px'}}>
                Adjust the start and end dates for this learning plan.
              </p>

              {hasCompletedAssignments && (
                <div style={{
                  marginBottom: '20px',
                  padding: '12px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#1565c0'
                }}>
                  ℹ️ Start date cannot be changed because assignments have been completed.
                </div>
              )}

              <div style={{marginBottom: '20px'}}>
                <label style={{display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500'}}>
                  Start Date {hasCompletedAssignments && <span style={{color: '#999', fontWeight: 'normal'}}>(locked)</span>}
                </label>
                <input
                  type="date"
                  value={formData.timeline.start}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    timeline: { ...prev.timeline, start: e.target.value }
                  }))}
                  disabled={hasCompletedAssignments}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    backgroundColor: hasCompletedAssignments ? '#f5f5f5' : 'white',
                    cursor: hasCompletedAssignments ? 'not-allowed' : 'text'
                  }}
                />
              </div>

              <div style={{marginBottom: '20px'}}>
                <label style={{display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500'}}>
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.timeline.end}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    timeline: { ...prev.timeline, end: e.target.value }
                  }))}
                  min={formData.timeline.start}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{
                padding: '15px',
                backgroundColor: '#f5f7fa',
                borderRadius: '6px'
              }}>
                <div style={{fontSize: '13px', color: '#666', marginBottom: '8px'}}>
                  Current: {formatDate(plan.timeline.start)} → {formatDate(plan.timeline.end)}
                </div>
                <div style={{fontSize: '14px', fontWeight: '500', color: '#333'}}>
                  New Duration: {calculateDuration()} days
                </div>
              </div>

              <div style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: '#fff3e0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#e65100'
              }}>
                ⚠️ Extending the timeline will redistribute pending assignments across the new dates.
              </div>
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div>
              <h3 style={{marginTop: 0, fontSize: '18px', color: '#333'}}>Schedule Settings</h3>
              <p style={{fontSize: '13px', color: '#666', marginBottom: '20px'}}>
                Select which days of the week to include in the schedule.
              </p>

              <div style={{marginBottom: '20px'}}>
                <label style={{display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '500'}}>
                  Days of Week ({formData.schedule.specificDays.length} selected)
                </label>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  {daysOfWeek.map(day => (
                    <label
                      key={day}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        border: `2px solid ${formData.schedule.specificDays.includes(day) ? '#2196f3' : '#ddd'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        backgroundColor: formData.schedule.specificDays.includes(day) ? '#e3f2fd' : 'white',
                        transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.schedule.specificDays.includes(day)}
                        onChange={() => toggleScheduleDay(day)}
                        style={{marginRight: '10px', width: '16px', height: '16px', cursor: 'pointer'}}
                      />
                      <span style={{fontSize: '14px', fontWeight: '500'}}>{day}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{
                padding: '15px',
                backgroundColor: '#f5f7fa',
                borderRadius: '6px'
              }}>
                <div style={{fontSize: '13px', color: '#666', marginBottom: '8px'}}>
                  Current: {plan.schedule.specificDays.join(', ')}
                </div>
                <div style={{fontSize: '14px', fontWeight: '500', color: '#333'}}>
                  New: {formData.schedule.specificDays.join(', ') || 'None selected'}
                </div>
              </div>

              <div style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: '#fff3e0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#e65100'
              }}>
                ⚠️ Changing schedule will shift pending assignment due dates to match the new days.
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div>
              <h3 style={{marginTop: 0, fontSize: '18px', color: '#333'}}>Plan Notes</h3>
              <p style={{fontSize: '13px', color: '#666', marginBottom: '20px'}}>
                Add or update notes for this learning plan.
              </p>

              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter notes about this plan (optional)..."
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 24px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f9f9f9'
        }}>
          <div style={{fontSize: '13px', color: '#666'}}>
            {hasChanges ? '✓ Changes detected' : 'No changes made'}
          </div>
          <div style={{display: 'flex', gap: '10px'}}>
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                backgroundColor: 'white',
                color: '#666',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitClick}
              disabled={loading || !hasChanges}
              style={{
                padding: '10px 24px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: hasChanges && !loading ? '#2196f3' : '#ccc',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: hasChanges && !loading ? 'pointer' : 'not-allowed'
              }}
            >
              {loading ? 'Updating...' : 'Update Plan'}
            </button>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmation && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              zIndex: 10
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '450px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
            >
              <h3 style={{marginTop: 0, marginBottom: '15px', color: '#d32f2f', fontSize: '20px'}}>
                ⚠️ Confirm Plan Update
              </h3>
              <p style={{marginBottom: '20px', color: '#666', lineHeight: '1.6', fontSize: '14px'}}>
                Updating this plan will <strong>regenerate all pending assignments</strong> based on your changes.
              </p>
              <div style={{
                padding: '12px',
                backgroundColor: '#fff3e0',
                borderRadius: '6px',
                marginBottom: '20px',
                fontSize: '13px',
                color: '#e65100'
              }}>
                <strong>What will happen:</strong>
                <ul style={{margin: '8px 0 0 0', paddingLeft: '20px'}}>
                  <li>Completed assignments will be preserved</li>
                  <li>Pending assignments will be deleted and regenerated</li>
                  <li>New due dates will be assigned based on your changes</li>
                </ul>
              </div>
              <p style={{marginBottom: '25px', fontSize: '14px', color: '#333'}}>
                Are you sure you want to continue?
              </p>
              <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                <button
                  onClick={() => setShowConfirmation(false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    backgroundColor: 'white',
                    color: '#666',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmedSubmit}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: '#2196f3',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Yes, Update Plan
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditLearningPlanModal;
